import { ExpertRole } from '@/features/experts/types/ExpertRole';
import { useUserStore } from '@/utils/stores/useUserStore';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { VenereButton } from '../../..';
import { VenereLogo } from './VenereLogo';
import { VenereUserMenu } from './VenereUserMenu';

interface VenereBannerProps {
  showBackButton?: boolean;
  onBackClick?: () => void;
  backButtonText?: string;
  showUserAvatar?: boolean;
  onUserAvatarClick?: () => void;
  className?: string;
  expertInfo?: {
    name: string;
    familyName: string;
    role: string;
    avatar?: string;
  };
}

export const VenereBanner: React.FC<VenereBannerProps> = ({
  showBackButton = false,
  onBackClick,
  backButtonText = 'Back',
  showUserAvatar = true,
  onUserAvatarClick,
  className = '',
  expertInfo,
}) => {
  const navigate = useNavigate();
  const { userData } = useUserStore();

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      navigate(-1);
    }
  };

  const handleUserAvatarClick = () => {
    if (onUserAvatarClick) {
      onUserAvatarClick();
    } else {
      navigate('/venere/account');
    }
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    const firstName = userData.name || '';
    const lastName = userData.familyName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <div className={`grid grid-cols-3 items-center px-4 md:px-8 py-3 ${className}`}>
      {/* Left: Back Button or User Avatar */}
      <div className="flex items-center space-x-3 min-w-[120px] h-10">
        {showBackButton ? (
          <VenereButton variant='back' onClick={handleBackClick} />
        ) : showUserAvatar ? (
          <div
            className="w-10 h-10 venere-bg-royal rounded-full flex items-center justify-center venere-text-matcha font-semibold text-sm cursor-pointer"
            onClick={handleUserAvatarClick}
          >
            {getUserInitials()}
          </div>
        ) : (
          <div className="w-10 h-10" />
        )}
      </div>

      {/* Center: Logo or Expert Name Only */}
      <div className="flex justify-center">
        {expertInfo ? (
          <div className="flex flex-col items-center">
            <div className="venere-text-primary font-semibold text-sm text-center">
              {expertInfo.role === ExpertRole.Admin ? "Customer Care" : `${expertInfo.name} ${expertInfo.familyName}`}
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-2">
            <VenereLogo width={170} height={32} />
          </div>
        )}
      </div>

      {/* Right: Expert Avatar or Menu */}
      <div className="flex items-center justify-end space-x-2 min-w-[48px] h-10">
        {expertInfo ? (
          // Expert Avatar on the right
          expertInfo.avatar ? (
            <img 
              src={expertInfo.avatar} 
              alt="Expert Avatar"
              className={`w-10 h-10 rounded-[20px] ${expertInfo.role === ExpertRole.Admin ? "border border-solid border-[var(--venere-royal)]" : ""} `}
              onError={(e) => {
                // Hide the image and show initials instead
                e.currentTarget.style.display = 'none';
                const parent = e.currentTarget.parentElement;
                if (parent) {
                  const fallback = document.createElement('div');
                  fallback.className = 'w-10 h-10 venere-bg-royal rounded-full flex items-center justify-center venere-text-matcha font-semibold text-sm';
                  fallback.textContent = `${expertInfo.name.charAt(0)}${expertInfo.familyName.charAt(0)}`;
                  parent.appendChild(fallback);
                }
              }}
            />
          ) : (
            <div className="w-10 h-10 venere-bg-royal rounded-full flex items-center justify-center venere-text-matcha font-semibold text-sm">
              {expertInfo.name.charAt(0)}{expertInfo.familyName.charAt(0)}
            </div>
          )
        ) : (
          // Menu when no expert info
          <VenereUserMenu 
            menuClassName="right-4 md:right-8"
          />
        )}
      </div>
    </div>
  );
};

export default VenereBanner; 